import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getProductSettings,
  setProductSetting,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProductTypes } from "@/context/ProductTypesContext";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Copy,
  Pencil,
  ClipboardList,
  CalendarClock,
  Snowflake,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const FIELD_TYPES = ["number", "text", "temperature", "ph", "time", "boolean", "select"];
const uid = () => Math.random().toString(36).slice(2, 10);

const emptyField = () => ({
  id: uid(),
  name: "",
  type: "number",
  unit: "",
  required: false,
  min: "",
  max: "",
  options: [],
  fail_options: [],
});

export default function Templates() {
  const { can } = useAuth();
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const canEdit = can("admin", "manager", "qa");
  const canManageSettings = can("admin", "manager");
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited
  const [fieldDialog, setFieldDialog] = useState(null); // field being added/edited
  const [settings, setSettings] = useState({}); // product_type -> settings row { shelf_life_days, maturation_days }

  const load = async () => setTemplates(await getTemplates());
  const loadSettings = async () => {
    const rows = await getProductSettings();
    const map = {};
    rows.forEach((r) => { map[r.product_type] = r; });
    setSettings(map);
  };
  useEffect(() => {
    (async () => { await load(); await loadSettings(); })();
  }, []);

  const startNew = () => {
    setEditing({ id: null, name: "", product_type: productTypes[0], description: "", fields: [] });
  };

  const cloneTemplate = (tm) => {
    setEditing({
      id: null,
      name: `${tm.name} ${t("tpl.copySuffix")}`,
      product_type: tm.product_type,
      description: tm.description,
      fields: tm.fields.map((f) => ({ ...f, id: uid() })),
    });
  };

  const editTemplate = (tm) => {
    setEditing({
      id: tm.id,
      name: tm.name,
      product_type: tm.product_type,
      description: tm.description || "",
      fields: tm.fields.map((f) => ({
        ...f,
        min: f.min == null ? "" : f.min,
        max: f.max == null ? "" : f.max,
        options: f.options || [],
        fail_options: f.fail_options || [],
      })),
      is_starter: tm.is_starter,
    });
  };

  const move = (idx, dir) => {
    setEditing((e) => {
      const fields = [...e.fields];
      const j = idx + dir;
      if (j < 0 || j >= fields.length) return e;
      [fields[idx], fields[j]] = [fields[j], fields[idx]];
      return { ...e, fields };
    });
  };

  const removeField = (id) =>
    setEditing((e) => ({ ...e, fields: e.fields.filter((f) => f.id !== id) }));

  const saveField = (field) => {
    setEditing((e) => {
      const exists = e.fields.some((f) => f.id === field.id);
      const fields = exists
        ? e.fields.map((f) => (f.id === field.id ? field : f))
        : [...e.fields, field];
      return { ...e, fields };
    });
    setFieldDialog(null);
  };

  const saveTemplate = async () => {
    if (!editing.name.trim()) {
      toast.error(t("tpl.nameRequired"));
      return;
    }
    const payload = {
      name: editing.name,
      product_type: editing.product_type,
      description: editing.description,
      fields: editing.fields.map((f) => ({
        id: f.id,
        name: f.name,
        type: f.type,
        unit: f.unit || "",
        required: !!f.required,
        min: f.min === "" || f.min == null ? null : Number(f.min),
        max: f.max === "" || f.max == null ? null : Number(f.max),
        options: f.type === "select" ? f.options : null,
        fail_options: f.type === "select" ? (f.fail_options || []).filter((o) => (f.options || []).includes(o)) : null,
      })),
    };
    try {
      if (editing.id) await updateTemplate(editing.id, payload);
      else await createTemplate(payload);
      toast.success(t("tpl.saved"));
      setEditing(null);
      load();
    } catch {
      toast.error(t("tpl.failedSave"));
    }
  };

  const removeTemplate = async (id) => {
    await deleteTemplate(id);
    toast.success(t("tpl.deleted"));
    load();
  };

  return (
    <Layout
      actions={
        canEdit ? (
          <Button size="sm" onClick={startNew} data-testid="new-template-button">
            <Plus className="mr-1.5 h-4 w-4" /> {t("tpl.newTemplate")}
          </Button>
        ) : null
      }
    >
      <div className="space-y-6">
        {/* Product targets — full width */}
        <ShelfLifeCard
          productTypes={Array.from(new Set([...productTypes, ...templates.map((tm) => tm.product_type)]))}
          settings={settings}
          canEdit={canManageSettings}
          onSaved={loadSettings}
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Template list */}
          <div className="space-y-3 lg:col-span-5">
            {templates.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center py-12 text-center">
                <ClipboardList className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("tpl.empty")}</p>
              </CardContent>
            </Card>
          )}
          {templates.map((tm) => {
            const isInvalidType = !productTypes.includes(tm.product_type);
            const canDelete = canEdit && (!tm.is_starter || isInvalidType);
            return (
            <Card key={tm.id} data-testid={`template-card-${tm.id}`} className={`${editing?.id === tm.id ? "ring-2 ring-[hsl(var(--brand))]" : ""} ${isInvalidType ? "border-[hsl(var(--warn,38_92%_50%))]" : ""}`}>
              <CardContent className="flex items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{tm.name}</span>
                    {tm.is_starter && <Badge variant="secondary" className="text-[10px]">{t("tpl.starter")}</Badge>}
                    {isInvalidType && (
                      <Badge variant="destructive" className="text-[10px]" data-testid={`template-invalid-${tm.id}`}>
                        {t("tpl.invalidProductType", { raw: tm.product_type })}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {isInvalidType ? tm.product_type : td(tm.product_type)} · {t("tpl.fieldsCount", { n: tm.fields.length })}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="icon" title={canEdit ? t("common.edit") : t("common.view")} onClick={() => editTemplate(tm)} data-testid={`template-edit-${tm.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {canEdit && (
                    <Button variant="ghost" size="icon" title={t("tpl.duplicate")} onClick={() => cloneTemplate(tm)} data-testid={`template-clone-${tm.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => removeTemplate(tm.id)} data-testid={`template-delete-${tm.id}`}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>

        {/* Builder */}
        <div className="lg:col-span-7">
          {!editing ? (
            <Card>
              <CardContent className="flex flex-col items-center py-16 text-center">
                <Pencil className="h-10 w-10 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">
                  {t("tpl.selectPrompt")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="template-builder">
              <CardHeader>
                <CardTitle className="text-base">{editing.id ? t("tpl.editTemplate") : t("tpl.newTemplate")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>{t("tpl.templateName")}</Label>
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("prod.productType")}</Label>
                    <Select value={editing.product_type} onValueChange={(v) => setEditing({ ...editing, product_type: v })}>
                      <SelectTrigger data-testid="template-product-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {productTypes.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("tpl.description")}</Label>
                  <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="template-desc-input" />
                </div>

                <div className="flex items-center justify-between border-t border-border pt-3">
                  <Label className="text-sm font-semibold">{t("tpl.fields")} ({editing.fields.length})</Label>
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={() => setFieldDialog(emptyField())} data-testid="add-field-button">
                      <Plus className="mr-1.5 h-4 w-4" /> {t("prod.addField")}
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {editing.fields.length === 0 && (
                    <p className="py-4 text-center text-sm text-muted-foreground">{t("tpl.noFields")}</p>
                  )}
                  {editing.fields.map((f, idx) => (
                    <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border p-3" data-testid={`field-row-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{f.name || t("tpl.unnamed")}</span>
                          <Badge variant="secondary" className="text-[10px]">{f.type}{f.unit ? ` · ${f.unit}` : ""}</Badge>
                          {f.required && <Badge className="bg-[hsl(var(--brand))] text-[10px]">{t("tpl.required")}</Badge>}
                        </div>
                        {(f.min !== "" && f.min != null) || (f.max !== "" && f.max != null) ? (
                          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {t("tpl.rangeLine", { min: f.min === "" || f.min == null ? "–" : f.min, max: f.max === "" || f.max == null ? "–" : f.max })}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center">
                        <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0}><ChevronUp className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === editing.fields.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setFieldDialog({ ...f })} data-testid={`field-edit-${idx}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => removeField(f.id)} data-testid={`field-delete-${idx}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-2 border-t border-border pt-4">
                  <Button variant="outline" onClick={() => setEditing(null)}>{canEdit ? t("common.cancel") : t("common.close")}</Button>
                  {canEdit && <Button onClick={saveTemplate} data-testid="save-template-button">{t("tpl.saveTemplate")}</Button>}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </div>


      {/* Field editor dialog */}
      <FieldDialog key={fieldDialog ? (fieldDialog.id ?? "new") : "closed"} field={fieldDialog} onClose={() => setFieldDialog(null)} onSave={saveField} />
    </Layout>
  );
}

const FieldDialog = ({ field, onClose, onSave }) => {
  const { t } = useT();
  const [f, setF] = useState(field);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const showRange = ["number", "temperature", "ph", "time"].includes(f.type);
  return (
    <Dialog open={!!field} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("tpl.fieldSettings")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("prod.fieldName")}</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name-input" placeholder={t("tpl.fieldNamePlaceholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("prod.type")}</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((ft) => <SelectItem key={ft} value={ft}>{ft}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.unit")}</Label>
              <Input value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="°C, %, pH..." data-testid="field-unit-input" />
            </div>
          </div>

          {showRange && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("tpl.minAcceptable")}</Label>
                <Input type="number" step="0.01" value={f.min} onChange={(e) => set("min", e.target.value)} className="text-right font-mono" data-testid="field-min-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("tpl.maxAcceptable")}</Label>
                <Input type="number" step="0.01" value={f.max} onChange={(e) => set("max", e.target.value)} className="text-right font-mono" data-testid="field-max-input" />
              </div>
            </div>
          )}

          {f.type === "select" && (
            <div className="space-y-1.5">
              <Label>{t("prod.options")}</Label>
              <Input
                value={(f.options || []).join(", ")}
                onChange={(e) => {
                  const opts = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                  setF((x) => ({
                    ...x, options: opts,
                    fail_options: (x.fail_options || []).filter((o) => opts.includes(o)),
                  }));
                }}
                placeholder="Pass, Fail, Rework"
                data-testid="field-options-input"
              />
              {(f.options || []).length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs text-muted-foreground">{t("tpl.optionConformity")}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {(f.options || []).map((o) => {
                      const fail = (f.fail_options || []).includes(o);
                      return (
                        <button key={o} type="button"
                          onClick={() => setF((x) => ({
                            ...x,
                            fail_options: fail
                              ? (x.fail_options || []).filter((y) => y !== o)
                              : [...(x.fail_options || []), o],
                          }))}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${fail
                            ? "border-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))]"
                            : "border-[hsl(var(--success))] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]"}`}
                          data-testid={`field-option-conformity-${o}`}
                          aria-pressed={fail}>
                          {fail ? <XCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          {o} · {fail ? t("tpl.optionFail") : t("tpl.optionPass")}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{t("tpl.optionConformityHint")}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label htmlFor="req">{t("tpl.requiredField")}</Label>
            <Switch id="req" checked={f.required} onCheckedChange={(v) => set("required", v)} data-testid="field-required-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => { if (!f.name.trim()) { toast.error(t("prod.fieldNameRequired")); return; } onSave(f); }} data-testid="save-field-button">{t("tpl.saveField")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Shelf life + maturation (days) per product type
//  - shelf life drives automatic expiration dates
//  - maturation drives the "ready for cold room" reminder (counted from when a run enters the maturation status)
function ShelfLifeCard({ productTypes, settings, canEdit, onSaved }) {
  const { t, td } = useT();
  const buildDraft = () => {
    const d = {};
    productTypes.forEach((pt) => {
      const row = settings[pt] || {};
      d[pt] = {
        shelf: row.shelf_life_days == null ? "" : String(row.shelf_life_days),
        delivery: row.delivery_shelf_life_days == null ? "" : String(row.delivery_shelf_life_days),
        minSale: row.min_sale_shelf_life_days == null ? "" : String(row.min_sale_shelf_life_days),
        mat: row.maturation_days == null ? "" : String(row.maturation_days),
        yield: row.target_yield_per_100l == null ? "" : String(row.target_yield_per_100l),
        sc: row.target_specific_consumption == null ? "" : String(row.target_specific_consumption),
        freezer: !!row.freezer_storage,
      };
    });
    return d;
  };
  const [draft, setDraft] = useState(buildDraft);
  // Re-sync the editable draft from props when the underlying settings/products change.
  // (Adjusting state during render — the documented React pattern, no effect needed.)
  const sig = JSON.stringify([settings, productTypes]);
  const [prevSig, setPrevSig] = useState(sig);
  if (sig !== prevSig) {
    setPrevSig(sig);
    setDraft(buildDraft());
  }

  const FIELD_META = {
    shelf: { apiKey: "shelf_life_days", label: t("tpl.shelfLife") },
    delivery: { apiKey: "delivery_shelf_life_days", label: t("tpl.deliveryShelfLife") },
    minSale: { apiKey: "min_sale_shelf_life_days", label: t("tpl.minSaleShelfLife") },
    mat: { apiKey: "maturation_days", label: t("prod.maturationLabel") },
    yield: { apiKey: "target_yield_per_100l", label: t("tpl.targetYield") },
    sc: { apiKey: "target_specific_consumption", label: t("tpl.targetSc") },
  };

  const save = async (pt, field) => {
    const { apiKey, label } = FIELD_META[field];
    const current = settings[pt]?.[apiKey] ?? null;
    const raw = draft[pt]?.[field];
    const val = raw === "" || raw == null ? null : Number(raw);
    if (val != null && (Number.isNaN(val) || val < 0)) {
      toast.error(t("tpl.validPositive"));
      return;
    }
    if (val === current) return;
    try {
      await setProductSetting(pt, { [apiKey]: val });
      toast.success(t("tpl.savedFor", { label, product: td(pt) }));
      onSaved();
    } catch {
      toast.error(t("settings.failedSave"));
    }
  };

  const setField = (pt, field, value) =>
    setDraft((d) => ({ ...d, [pt]: { ...(d[pt] || {}), [field]: value } }));

  const saveFreezer = async (pt, value) => {
    setField(pt, "freezer", value);
    try {
      await setProductSetting(pt, { freezer_storage: value });
      toast.success(t("tpl.savedFor", { label: t("tpl.freezerStorage"), product: td(pt) }));
      onSaved();
    } catch {
      setField(pt, "freezer", !value);
      toast.error(t("settings.failedSave"));
    }
  };

  const numCell = (pt, field, unit, testid) => (
    <div className="relative w-24">
      <Input
        type="number"
        min="0"
        step={field === "yield" || field === "sc" ? "0.1" : "1"}
        value={draft[pt]?.[field] ?? ""}
        disabled={!canEdit}
        onChange={(e) => setField(pt, field, e.target.value)}
        onBlur={() => canEdit && save(pt, field)}
        placeholder={t("tpl.notSet")}
        className="h-8 pr-9 text-right font-mono"
        data-testid={`${testid}-${pt}`}
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">{unit}</span>
    </div>
  );

  return (
    <Card data-testid="shelf-life-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("tpl.productTargets")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t("tpl.targetsHint")}
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto thin-scroll rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="sticky left-0 z-10 min-w-[160px] bg-muted/40 font-semibold text-foreground">
                  {t("common.product")}
                </TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("tpl.shelfLife")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("tpl.deliveryShelfLife")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("tpl.minSaleShelfLife")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("prod.maturationLabel")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("tpl.targetYield")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">{t("tpl.targetSc")}</TableHead>
                <TableHead className="whitespace-nowrap text-center">
                  <span className="inline-flex items-center gap-1.5">
                    <Snowflake className="h-3.5 w-3.5 text-[hsl(var(--brand))]" /> {t("tpl.freezerStorage")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productTypes.map((pt) => (
                <TableRow key={pt} data-testid={`shelf-life-row-${pt}`} className="group">
                  <TableCell className="sticky left-0 z-10 min-w-[160px] whitespace-nowrap bg-card font-medium group-hover:bg-muted/50">
                    {td(pt)}
                  </TableCell>
                  <TableCell className="text-center">{numCell(pt, "shelf", "d", "shelf-life-input")}</TableCell>
                  <TableCell className="text-center">{numCell(pt, "delivery", "d", "delivery-shelf-life-input")}</TableCell>
                  <TableCell className="text-center">{numCell(pt, "minSale", "d", "min-sale-shelf-life-input")}</TableCell>
                  <TableCell className="text-center">{numCell(pt, "mat", "d", "maturation-input")}</TableCell>
                  <TableCell className="text-center">{numCell(pt, "yield", "kg", "target-yield-input")}</TableCell>
                  <TableCell className="text-center">{numCell(pt, "sc", "kg/kg", "target-sc-input")}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <Switch
                        checked={!!draft[pt]?.freezer}
                        disabled={!canEdit}
                        onCheckedChange={(v) => canEdit && saveFreezer(pt, v)}
                        data-testid={`freezer-storage-switch-${pt}`}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-3 space-y-1">
          <p className="text-[11px] text-muted-foreground">{t("tpl.targetYieldHint")}</p>
          <p className="text-[11px] text-muted-foreground">{t("tpl.targetScHint")}</p>
          <p className="text-[11px] text-muted-foreground">{t("tpl.freezerStorageHint")}</p>
          {!canEdit && (
            <p className="pt-1 text-xs text-muted-foreground">{t("tpl.adminOnly")}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
